import { z } from 'zod'

import { AiListingJobStatus, ImageCategory } from '@/client'

/**
 * The view state both AI listing pages read from the URL.
 *
 * Declared beside the pages rather than in the route files, so imports keep
 * pointing one way and the logic is covered. Every field is optional with a
 * `.catch(undefined)`: a query string is user input, and a default written
 * into every URL makes the clean view and the filtered one indistinguishable.
 */

const statuses = Object.values(AiListingJobStatus) as [AiListingJobStatus, ...AiListingJobStatus[]]
const categories = Object.values(ImageCategory) as [ImageCategory, ...ImageCategory[]]

export const PAGE_SIZES = [10, 20, 50] as const

export const jobsSearchSchema = z.object({
  page: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The page of "Your listings"; 1 by default.'),
  limit: z
    .number()
    .int()
    .min(1)
    .max(100)
    .optional()
    .catch(undefined)
    .describe('Listings per page (the Rows menu offers 10, 20 and 50); 20 by default.'),
  status: z
    .enum(statuses)
    .optional()
    .catch(undefined)
    .describe('Only the listings at this pipeline step; every status when absent.'),
})

export type JobsSearch = z.infer<typeof jobsSearchSchema>

export const JOBS_SEARCH_DEFAULTS = { page: 1, limit: 20 } as const

/** Drop any value that equals its default, so a URL carries only what differs. */
export function withoutJobsSearchDefaults(search: JobsSearch): JobsSearch {
  const pruned: JobsSearch = {}
  if (search.page !== undefined && search.page !== JOBS_SEARCH_DEFAULTS.page) {
    pruned.page = search.page
  }
  if (search.limit !== undefined && search.limit !== JOBS_SEARCH_DEFAULTS.limit) {
    pruned.limit = search.limit
  }
  if (search.status !== undefined) pruned.status = search.status
  return pruned
}

export const IMAGE_VIEWS = ['grid', 'carousel'] as const
export type ImageView = (typeof IMAGE_VIEWS)[number]

/** Amazon's own images get a tab of their own beside the generated categories. */
export const ORIGINALS_TAB = 'originals'
const imageTabs = [...categories, ORIGINALS_TAB] as [
  ImageCategory | typeof ORIGINALS_TAB,
  ...Array<ImageCategory | typeof ORIGINALS_TAB>,
]
export type ImageTab = (typeof imageTabs)[number]

/** The job page's guide, in the order a seller works through it. */
export const JOB_STEPS = ['photos', 'text', 'download'] as const
export type JobStep = (typeof JOB_STEPS)[number]

/** The run's notes: folded away, open, or open with each note in full. */
export const NOTES_OPEN = 1
export const NOTES_DETAILS = 2

/**
 * The job page's view state.
 *
 * `step` is the guide's current step. `tab` and `view` choose what the photo
 * step shows; `slide` is the image the carousel is on; `preview` names the
 * image open in the lightbox — a generated image's id, or `original-<n>` for
 * one of Amazon's, which have no id — so an enlarged image is a link like any
 * other record. `notes` is `1` with the run's notes open and `2` with each
 * shown in full; `more` is `1` with the bullet suggestions not in use shown.
 * Each is absent when closed.
 */
export const jobSearchSchema = z.object({
  step: z
    .enum(JOB_STEPS)
    .optional()
    .catch(undefined)
    .describe(
      'The step of the guide on screen: choose photos, review text or download; photos by default.',
    ),
  tab: z
    .enum(imageTabs)
    .optional()
    .catch(undefined)
    .describe(
      "The photo tab: lifestyle, product, infographic or Amazon's originals; lifestyle by default.",
    ),
  view: z
    .enum(IMAGE_VIEWS)
    .optional()
    .catch(undefined)
    .describe('How the photo tab lays its images out: a grid or one at a time; grid by default.'),
  slide: z
    .number()
    .int()
    .min(0)
    .optional()
    .catch(undefined)
    .describe('The image the one-at-a-time view is on, counted from 0; 0 by default.'),
  preview: z
    .string()
    .min(1)
    .max(200)
    .optional()
    .catch(undefined)
    .describe(
      'The image open in the lightbox: a generated image id, or original-<n> for an Amazon photo.',
    ),
  notes: z
    .union([z.literal(NOTES_OPEN), z.literal(NOTES_DETAILS)])
    .optional()
    .catch(undefined)
    .describe("The run's notes: 1 open, 2 open with every note in full; folded when absent."),
  more: z
    .literal(1)
    .optional()
    .catch(undefined)
    .describe('1 shows the bullet-point suggestions not in use.'),
})

export type JobSearch = z.infer<typeof jobSearchSchema>

export const JOB_SEARCH_DEFAULTS = {
  step: 'photos',
  tab: 'lifestyle',
  view: 'grid',
  slide: 0,
} as const

export function withoutJobSearchDefaults(search: JobSearch): JobSearch {
  const pruned: JobSearch = {}
  if (search.step !== undefined && search.step !== JOB_SEARCH_DEFAULTS.step) {
    pruned.step = search.step
  }
  if (search.tab !== undefined && search.tab !== JOB_SEARCH_DEFAULTS.tab) pruned.tab = search.tab
  if (search.view !== undefined && search.view !== JOB_SEARCH_DEFAULTS.view) {
    pruned.view = search.view
  }
  if (search.slide !== undefined && search.slide !== JOB_SEARCH_DEFAULTS.slide) {
    pruned.slide = search.slide
  }
  if (search.preview !== undefined) pruned.preview = search.preview
  if (search.notes !== undefined) pruned.notes = search.notes
  if (search.more !== undefined) pruned.more = search.more
  return pruned
}
